// Solicitudes de receta de continuación (staff). Centro Medico → Clínica → Solicitudes de receta.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { SolicitudRecetaRow, fieldCls, medRpc } from './shared';

const ESTADOS = ['pendiente', 'aprobada', 'rechazada'];
const cls: Record<string, string> = { pendiente: 'bg-pendiente-soft text-pendiente', aprobada: 'bg-estable-soft text-estable', rechazada: 'bg-urgente-soft text-urgente' };

export function SolicitudesReceta() {
  const qc = useQueryClient();
  const [estado, setEstado] = useState('pendiente');
  const { data: rows = [], isFetching } = useQuery<SolicitudRecetaRow[]>({
    queryKey: ['med-sol-receta', estado], queryFn: () => medRpc<SolicitudRecetaRow[]>('med_solicitudes_receta', { _estado: estado }),
  });
  const resolver = useMutation({
    mutationFn: ({ id, aprobar, respuesta }: { id: string; aprobar: boolean; respuesta: string }) =>
      medRpc('med_resolver_solicitud_receta', { _id: id, _aprobar: aprobar, _respuesta: respuesta }),
    onSuccess: () => { toast.success('Solicitud resuelta'); void qc.invalidateQueries({ queryKey: ['med-sol-receta'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold">Solicitudes de receta</h1>
      <div className="inline-flex rounded-md border overflow-hidden">
        {ESTADOS.map((s) => (
          <button key={s} onClick={() => setEstado(s)} aria-pressed={estado === s} className={`px-3 py-1.5 text-sm capitalize ${estado === s ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{s}</button>
        ))}
      </div>
      <Card>
        <CardContent className="pt-4 space-y-2">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          {rows.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Sin solicitudes</p>}
          {rows.map((r) => (
            <div key={r.id} className="rounded-md border p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium">{r.paciente}</span>
                <span className={`rounded px-2 py-0.5 text-xs capitalize ${cls[r.estado] ?? ''}`}>{r.estado}</span>
              </div>
              <p className="text-muted-foreground mt-1">{r.descripcion}</p>
              {r.receta_origen_codigo && <p className="text-xs mt-1">Receta origen: {r.receta_origen_codigo} {r.origen_renovable ? '(renovable)' : '(no renovable)'}</p>}
              {r.respuesta && <p className="text-xs mt-1 italic">Respuesta: {r.respuesta}</p>}
              {r.estado === 'pendiente' && <Acciones id={r.id} onResolver={(aprobar, respuesta) => resolver.mutate({ id: r.id, aprobar, respuesta })} pend={resolver.isPending} />}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function Acciones({ onResolver, pend }: { id: string; onResolver: (aprobar: boolean, respuesta: string) => void; pend: boolean }) {
  const [resp, setResp] = useState('');
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <input className={`${fieldCls} h-9 flex-1 min-w-[12rem]`} placeholder="Respuesta al paciente (opcional)" value={resp} onChange={(e) => setResp(e.target.value)} />
      <Button size="sm" isLoading={pend} onClick={() => onResolver(true, resp)}><Check className="w-4 h-4 mr-1" /> Aprobar</Button>
      <Button size="sm" variant="destructive" isLoading={pend} onClick={() => onResolver(false, resp)}><X className="w-4 h-4 mr-1" /> Rechazar</Button>
    </div>
  );
}
